import { itensValidos, textoPreenchido } from './manual-utils';
import type { ManualData, SecaoId } from './manual-types';

export interface Requisito {
  rotulo: string;
  ok: boolean;
  critico: boolean;
}

export interface AvaliacaoSecao {
  id: SecaoId;
  percentual: number;
  requisitos: Requisito[];
  pendencias: Requisito[];
}

export interface AvaliacaoManual {
  geral: number;
  secoes: Record<SecaoId, AvaliacaoSecao>;
  pendenciasCriticas: number;
}

const check = (rotulo: string, ok: boolean, critico = false): Requisito => ({ rotulo, ok, critico });

function avaliarSecoes(dados: ManualData): Record<SecaoId, Requisito[]> {
  return {
    '01': [
      check('Nome da máquina', textoPreenchido(dados.nomeMaquina), true),
      check('Identificação / TAG', textoPreenchido(dados.identificacao), true),
      check('Modelo ou tipo', textoPreenchido(dados.modeloTipo)),
      check('Código de rastreabilidade', textoPreenchido(dados.codigoRastreabilidade), true),
      check('Fabricante', textoPreenchido(dados.fabricante)),
      check('Contratante', textoPreenchido(dados.contratante), true),
      check('Data e local do levantamento', textoPreenchido(dados.dataLevantamento) && textoPreenchido(dados.localLevantamento)),
      check('Documentos consultados', itensValidos(dados.documentosConsultados, ['texto']).length > 0),
      check('Público-alvo', itensValidos(dados.publicoAlvo, ['texto']).length > 0),
      check('Normas aplicáveis', !dados.opcoes.incluirNormas || itensValidos(dados.normas, ['codigo', 'titulo']).length > 0),
    ],
    '02': [
      check('Uso devido', textoPreenchido(dados.usoDevido), true),
      check('Usos proibidos', itensValidos(dados.usosProibidos, ['texto']).length > 0, true),
      check('Precauções gerais', itensValidos(dados.precaucoes, ['texto']).length > 0),
      check('EPC instalados', itensValidos(dados.epcs, ['texto']).length > 0),
      check('EPI obrigatórios', itensValidos(dados.epis, ['texto']).length > 0, true),
      check('Treinamentos exigidos', itensValidos(dados.treinamentos, ['texto']).length > 0),
      check('Sinalização aplicada', itensValidos(dados.sinalizacao, ['tipo', 'local']).length > 0),
      check('Fontes de energia', itensValidos(dados.energias, ['tipo', 'especificacao']).length > 0, true),
      check('Funções de segurança', itensValidos(dados.funcoesSeguranca, ['denominacao', 'componente']).length > 0),
      check('Matriz de perigos', itensValidos(dados.perigos, ['perigo', 'fonte']).length > 0, true),
      check('Situações de emergência', itensValidos(dados.emergencias, ['situacao', 'acao']).length > 0),
    ],
    '03': [
      check('Descrição funcional', textoPreenchido(dados.descricao), true),
      check('Aplicação / processo', textoPreenchido(dados.aplicacao), true),
      check('Princípio de funcionamento', textoPreenchido(dados.principioFuncionamento)),
      check('Vistas da máquina', itensValidos(dados.vistas, ['titulo']).length > 0, true),
      check('Imagens carregadas nas vistas', dados.vistas.some((vista) => Boolean(vista.imagem))),
      check('Especificações gerais', itensValidos(dados.especGerais, ['item', 'valor']).length > 0, true),
      check('Especificações elétricas', !dados.opcoes.incluirEspecEletricas || itensValidos(dados.especEletricas, ['item', 'valor']).length > 0),
      check('Especificações hidráulicas', !dados.opcoes.incluirEspecHidraulicas || itensValidos(dados.especHidraulicas, ['item', 'valor']).length > 0),
      check('Especificações pneumáticas', !dados.opcoes.incluirEspecPneumaticas || itensValidos(dados.especPneumaticas, ['item', 'valor']).length > 0),
    ],
    '04': [
      check('Transporte', textoPreenchido(dados.transporte)),
      check('Armazenagem', textoPreenchido(dados.armazenagem)),
      check('Montagem e instalação', textoPreenchido(dados.montagem)),
      check('Içamento e elevação', textoPreenchido(dados.icamento)),
      check('Ajustes originais', textoPreenchido(dados.ajustes)),
      check('Requisitos do local', itensValidos(dados.requisitosLocal, ['texto']).length > 0),
      check('Assistência técnica', !dados.opcoes.incluirAssistencia || textoPreenchido(dados.assistencia)),
    ],
    '05': [
      check('Apresentação da operação', textoPreenchido(dados.apresentacaoOperacao)),
      check('Comandos e indicadores', itensValidos(dados.comandos, ['identificacao', 'funcao']).length > 0, true),
      check('Sequência de preparação', itensValidos(dados.preparacao, ['descricao']).length > 0, true),
      check('Sequência de operação', itensValidos(dados.operacao, ['descricao']).length > 0, true),
      check('Sequência de parada', itensValidos(dados.parada, ['descricao']).length > 0),
      check('Atuação em emergência', itensValidos(dados.emergenciaEtapas, ['descricao']).length > 0, true),
      check('Mau uso razoavelmente previsível', !dados.opcoes.incluirUsoImprevisivel || itensValidos(dados.usosImprevisiveis, ['texto']).length > 0),
    ],
    '06': [
      check('Orientação geral de manutenção', textoPreenchido(dados.orientacaoManutencao)),
      check('Plano de manutenção', itensValidos(dados.planoManutencao, ['parte', 'tarefa']).length > 0, true),
      check('Plano de lubrificação', !dados.opcoes.incluirLubrificacao || itensValidos(dados.lubrificacao, ['ponto', 'produto']).length > 0),
      check('Rotina de limpeza', itensValidos(dados.limpeza, ['texto']).length > 0),
      check('Lista de falhas', itensValidos(dados.falhas, ['falha', 'solucao']).length > 0, true),
    ],
    '07': [
      check('Desativação', textoPreenchido(dados.desativacao)),
      check('Desmontagem', textoPreenchido(dados.desmontagem)),
      check('Descarte', textoPreenchido(dados.descarte)),
      check('Glossário', !dados.opcoes.incluirGlossario || itensValidos(dados.glossario, ['termo', 'definicao']).length > 0),
      check('Abreviaturas', !dados.opcoes.incluirAbreviaturas || itensValidos(dados.abreviaturas, ['termo', 'definicao']).length > 0),
    ],
    '08': [
      check('Título do documento', textoPreenchido(dados.opcoes.tituloDocumento)),
      check('Aviso legal do rodapé', textoPreenchido(dados.opcoes.avisoLegal)),
      check('Versão e data de emissão', textoPreenchido(dados.versao) && textoPreenchido(dados.dataEmissao)),
      check('Imagem de capa', Boolean(dados.imagemCapa)),
    ],
  };
}

export function avaliarManual(dados: ManualData): AvaliacaoManual {
  const bruto = avaliarSecoes(dados);
  const secoes = {} as Record<SecaoId, AvaliacaoSecao>;
  let totalItens = 0;
  let totalOk = 0;
  let pendenciasCriticas = 0;

  for (const [id, requisitos] of Object.entries(bruto) as [SecaoId, Requisito[]][]) {
    const ok = requisitos.filter((requisito) => requisito.ok).length;
    const pendencias = requisitos.filter((requisito) => !requisito.ok);
    totalItens += requisitos.length;
    totalOk += ok;
    pendenciasCriticas += pendencias.filter((requisito) => requisito.critico).length;
    secoes[id] = {
      id,
      percentual: requisitos.length ? Math.round((ok / requisitos.length) * 100) : 100,
      requisitos,
      pendencias,
    };
  }

  return {
    geral: totalItens ? Math.round((totalOk / totalItens) * 100) : 0,
    secoes,
    pendenciasCriticas,
  };
}
